import { cn } from '@tempo-io/sui-class-utils';
import { Circle, CircleFilled } from '@tempo-io/sui-icons';
import { useEffect, useRef, useState } from 'react';

export const CustomFieldRating = ({
  value,
  onChange,
  max = 5,
  readOnly,
  disabled,
  autoFocus,
  className,
  'data-testid': dataTestId,
  'data-dd-action-name': ddActionName,
}: {
  value?: number | null;
  onChange?: (value: number | null) => void;
  max?: number;
  readOnly?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  'data-testid'?: string;
  'data-dd-action-name'?: string;
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const current = value && !Number.isNaN(value) ? value : 0;
  const interactive = !readOnly && !disabled && onChange !== undefined;
  const displayed = interactive && hovered !== null ? hovered : current;
  const focusTargetRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (autoFocus) focusTargetRef.current?.focus();
  }, [autoFocus]);

  return (
    <span
      className={cn('inline-flex text-icon-brand-primary', className)}
      data-testid={dataTestId}
      data-dd-action-name={ddActionName}
    >
      {Array.from({ length: max }, (_, index) => {
        const rating = index + 1;
        const Icon = rating <= displayed ? CircleFilled : Circle;
        if (!interactive) return <Icon key={rating} className="size-6" />;
        return (
          <button
            key={rating}
            type="button"
            aria-label={String(rating)}
            aria-pressed={current === rating}
            ref={rating === (current || 1) ? focusTargetRef : undefined}
            className="flex cursor-pointer rounded-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus-neutral"
            onMouseEnter={() => setHovered(rating)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(rating)}
            onBlur={() => setHovered(null)}
            onClick={() => onChange(current === rating ? null : rating)}
          >
            <Icon className="size-6" />
          </button>
        );
      })}
    </span>
  );
};
